import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, Loader2, Download } from 'lucide-react';
import { format, type Locale } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { supabase } from '@/integrations/supabase/client';
interface OrderPrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: {
    id: string;
    order_number: string;
    qr_code?: string | null;
    pickup_address?: string | null;
    pickup_contact: string;
    pickup_phone: string;
    pickup_date: string;
    delivery_address?: string | null;
    delivery_contact: string;
    delivery_phone: string;
    delivery_date: string;
    cargo_title?: string | null;
    cargo_description?: string;
    cargo_picture?: string | null;
    weight_kg: number;
    trailer_type: string;
    pallets_count?: number | null;
    volume_m3?: number | null;
    cargo_unit_type?: string;
    special_instructions?: string | null;
    start_pictures?: string[] | null;
    start_details?: string;
    delivery_pictures?: string[] | null;
    delivery_details?: string;
    transport_jobs?: Array<{
      id: string;
      companies: {
        name: string;
        email: string;
        phone: string;
      };
      drivers: {
        license_number: string;
        users: {
          first_name: string;
          last_name: string;
          phone: string;
        };
      } | null;
      vehicles: {
        plate_number: string;
        brand: string;
        model: string;
        year: number;
        vehicle_types: {
          name: string;
          trailer_type: string;
        };
      } | null;
    }> | null;
  };
}
export const OrderPrintDialog = ({
  open,
  onOpenChange,
  order
}: OrderPrintDialogProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const {
    toast
  } = useToast();
  const {
    t,
    i18n
  } = useTranslation();
  const localeMap: Record<string, Locale> = {
    fr: fr,
    en: enUS,
    es: es,
    de: de,
    it: it
  };
  const currentLocale = localeMap[i18n.language] || fr;
  const generateCanvas = async () => {
    const printArea = document.getElementById('print-area');
    if (!printArea) {
      toast({
        title: t('orders.print.error'),
        description: t('orders.print.elementNotFound'),
        variant: 'destructive'
      });
      return null;
    }
    try {
      const canvas = await html2canvas(printArea, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      return canvas;
    } catch (error) {
      console.error('Screenshot error:', error);
      toast({
        title: t('orders.print.error'),
        description: t('orders.print.generationFailed'),
        variant: 'destructive'
      });
      return null;
    }
  };
  const handleScreenshot = async () => {
    setIsGenerating(true);
    const canvas = await generateCanvas();
    if (canvas) {
      canvas.toBlob(blob => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `bon-commande-${order.order_number}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast({
            title: t('orders.print.success'),
            description: t('orders.print.downloaded')
          });
        }
      }, 'image/png');
    }
    setIsGenerating(false);
  };
  const handlePrint = async () => {
    setIsPrinting(true);
    const canvas = await generateCanvas();
    if (canvas) {
      const imgData = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Impression - ${order.order_number}</title>
              <style>
                body { margin: 0; padding: 0; }
                img { width: 100%; height: auto; }
                @media print {
                  body { margin: 0; }
                  img { width: 100%; height: auto; page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <img src="${imgData}" onload="window.print();" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
    setIsPrinting(false);
  };
  return <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
              {t('orders.print.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-end gap-3 mb-4">
            <Button onClick={handlePrint} disabled={isPrinting || isGenerating} variant="outline" className="border-[#0880AE] text-[#0880AE] hover:bg-[#EBF4F8] disabled:opacity-50">
              {isPrinting ? <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  {t('orders.print.printing')}
                </> : <>
                  <Printer className="mr-2" size={18} />
                  {t('orders.print.printButton')}
                </>}
            </Button>
            
            <Button onClick={handleScreenshot} disabled={isGenerating || isPrinting} className="bg-[#0880AE] hover:bg-[#076A8E] text-white disabled:opacity-50">
              {isGenerating ? <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  {t('orders.print.generating')}
                </> : <>
                  <Download className="mr-2" size={18} />
                  {t('orders.print.button')}
                </>}
            </Button>
          </div>

          <div id="print-area" className="p-8 bg-white" style={{
          width: '210mm',
          minHeight: '297mm',
          fontSize: '15px'
        }}>
            {/* En-tête */}
            <div className="flex items-center justify-between border-b-2 border-[#DBE2EA] pb-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0880AE] rounded flex items-center justify-center">
                  <span className="text-white font-bold text-base">V</span>
                </div>
                <span className="font-bold text-2xl text-[#37699E] font-['Lexend_Deca']">VELOX</span>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#5A6B7A]">{t('common.order')}</p>
                <p className="text-xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {order.order_number}
                </p>
              </div>
              {order.qr_code && <div className="border-2 border-[#37699E] rounded p-2">
                  <QRCodeSVG value={order.qr_code} size={80} />
                </div>}
            </div>

            {/* Itinéraire: Enlèvement et Livraison côte à côte */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Enlèvement */}
              <div className="border border-[#DBE2EA] rounded-lg p-4">
                <h3 className="text-base font-semibold text-[#37699E] font-['Lexend_Deca'] mb-3">
                  📍 {t('orders.print.pickupInfo')}
                </h3>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.date')}:</span>{' '}
                  <span className="text-[#5A6B7A]">
                    {format(new Date(order.pickup_date), 'dd/MM/yyyy HH:mm', {
                    locale: currentLocale
                  })}
                  </span>
                </p>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.address')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.pickup_address || t('orders.print.notSpecified')}</span>
                </p>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.contact')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.pickup_contact}</span>
                </p>
                <p>
                  <span className="font-medium text-[#2D3748]">{t('common.phone')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.pickup_phone}</span>
                </p>
                {order.start_details && <p className="text-xs text-[#5A6B7A] italic mt-2 p-2 bg-[#EBF4F8] rounded">
                    {order.start_details}
                  </p>}
              </div>

              {/* Livraison */}
              <div className="border border-[#DBE2EA] rounded-lg p-4">
                <h3 className="text-base font-semibold text-[#37699E] font-['Lexend_Deca'] mb-3">
                  🎯 {t('orders.print.deliveryInfo')}
                </h3>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.date')}:</span>{' '}
                  <span className="text-[#5A6B7A]">
                    {format(new Date(order.delivery_date), 'dd/MM/yyyy HH:mm', {
                    locale: currentLocale
                  })}
                  </span>
                </p>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.address')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.delivery_address || t('orders.print.notSpecified')}</span>
                </p>
                <p className="mb-2">
                  <span className="font-medium text-[#2D3748]">{t('common.contact')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.delivery_contact}</span>
                </p>
                <p>
                  <span className="font-medium text-[#2D3748]">{t('common.phone')}:</span>{' '}
                  <span className="text-[#5A6B7A]">{order.delivery_phone}</span>
                </p>
                {order.delivery_details && <p className="text-xs text-[#5A6B7A] italic mt-2 p-2 bg-[#EBF4F8] rounded">
                    {order.delivery_details}
                  </p>}
              </div>
            </div>

            {/* Marchandise et Transporteur côte à côte */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Marchandise */}
              <div className="border border-[#DBE2EA] rounded-lg p-4">
                <h3 className="text-base font-semibold text-[#37699E] font-['Lexend_Deca'] mb-3">
                  📦 {t('orders.print.cargoInfo')}
                </h3>
                <div className="space-y-2">
                  {order.cargo_title && <p>
                      <span className="font-medium text-[#2D3748]">{t('orders.print.title_field')}:</span>{' '}
                      <span className="text-[#5A6B7A]">{order.cargo_title}</span>
                    </p>}
                  <p>
                    <span className="font-medium text-[#2D3748]">{t('common.description')}:</span>{' '}
                    <span className="text-[#5A6B7A]">{order.cargo_description}</span>
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs mt-3">
                    <span className="px-3 py-1.5 bg-[#EBF4F8] rounded">⚖️ {order.weight_kg} kg</span>
                    {order.cargo_unit_type && order.pallets_count && <span className="px-3 py-1.5 bg-[#EBF4F8] rounded">📦 {order.pallets_count} {order.cargo_unit_type}</span>}
                    {order.volume_m3 && <span className="px-3 py-1.5 bg-[#EBF4F8] rounded">📐 {order.volume_m3} m³</span>}
                    <span className="px-3 py-1.5 bg-[#EBF4F8] rounded">🚛 {order.trailer_type}</span>
                  </div>
                  {order.cargo_picture && <div className="mt-3">
                      <img src={supabase.storage.from('order-images').getPublicUrl(order.cargo_picture).data.publicUrl} alt="Cargo" className="max-h-32 rounded border border-[#DBE2EA]" />
                    </div>}
                </div>
              </div>

              {/* Transporteur */}
              {order.transport_jobs && order.transport_jobs.length > 0 && <div className="border border-[#0880AE] bg-[#0880AE]/5 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-[#0880AE] font-['Lexend_Deca'] mb-3">
                    🚚 {t('orders.details.transporterInfo')}
                  </h3>
                  <div className="space-y-3">
                    {/* Entreprise */}
                    <div>
                      <p className="font-medium text-[#2D3748] mb-1">{t('orders.details.company')}</p>
                      <p className="text-[#5A6B7A] mb-1">{order.transport_jobs[0].companies.name}</p>
                      <p className="text-xs text-[#5A6B7A]">
                        📧 {order.transport_jobs[0].companies.email}
                      </p>
                      <p className="text-xs text-[#5A6B7A]">
                        📞 {order.transport_jobs[0].companies.phone}
                      </p>
                    </div>

                    {/* Chauffeur */}
                    {order.transport_jobs[0].drivers && <div>
                        <p className="font-medium text-[#2D3748] mb-1">{t('orders.details.assignedDriver')}</p>
                        <p className="text-[#5A6B7A] mb-1">
                          {order.transport_jobs[0].drivers.users.first_name} {order.transport_jobs[0].drivers.users.last_name}
                        </p>
                        <p className="text-xs text-[#5A6B7A]">
                          📞 {order.transport_jobs[0].drivers.users.phone}
                        </p>
                        <p className="text-xs text-[#5A6B7A]">
                          🪪 {order.transport_jobs[0].drivers.license_number}
                        </p>
                      </div>}

                    {/* Véhicule */}
                    {order.transport_jobs[0].vehicles && <div>
                        <p className="font-medium text-[#2D3748] mb-1">{t('orders.details.vehicle')}</p>
                        <p className="text-[#5A6B7A] mb-1">
                          {order.transport_jobs[0].vehicles.brand} {order.transport_jobs[0].vehicles.model} ({order.transport_jobs[0].vehicles.year})
                        </p>
                        <p className="text-xs text-[#5A6B7A]">
                          🚗 {order.transport_jobs[0].vehicles.plate_number}
                        </p>
                        <p className="text-xs text-[#5A6B7A]">
                          {order.transport_jobs[0].vehicles.vehicle_types.name} • {order.transport_jobs[0].vehicles.vehicle_types.trailer_type}
                        </p>
                      </div>}
                  </div>
                </div>}
            </div>

            {/* Images */}
            {order.start_pictures && order.start_pictures.length > 0 || order.delivery_pictures && order.delivery_pictures.length > 0}

            {/* Instructions spéciales */}
            {order.special_instructions && <div className="border border-[#F2AC57] bg-[#F2AC57]/10 rounded-lg p-4 mb-6">
                <h3 className="text-base font-semibold text-[#F2AC57] font-['Lexend_Deca'] mb-2">
                  ⚠️ {t('orders.print.additionalInstructions')}
                </h3>
                <p className="text-[#2D3748]">{order.special_instructions}</p>
              </div>}

            {/* Footer */}
            <div className="border-t border-[#DBE2EA] pt-3 text-center text-xs text-[#5A6B7A] mt-auto">
              <p>{t('orders.print.platformDescription')}</p>
              <p>{t('orders.print.generatedOn')} {format(new Date(), 'dd/MM/yyyy HH:mm', {
                locale: currentLocale
              })}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>;
};